<template>
  <div class="ak-variables-textarea">
    <ak-form>
      <ak-field class="mb-none">
        <ak-input
          ref="input"
          v-model="computedValue"
          type="textarea"
          :rows="rows"
          @blur="setCursorPosition"
        />
      </ak-field>
    </ak-form>

    <ak-popover theme="light" width="584px">
      <template #trigger>
        <ak-fab icon="plus" size="small" />
      </template>
      <template #popover>
        <ak-variables
          :account="account"
          :agency="agency"
          :insert="insert"
          :size="size"
          :placement="placement"
          :theme="theme"
        />
      </template>
    </ak-popover>
  </div>
</template>

<script>
import { defineComponent, ref, computed } from 'vue'
import Variables from './Variables.vue'
import Input from '../input/Input.vue'
import Form from '../form/Form.vue'
import Field from '../field/Field.vue'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-variables-textarea />
   */
  name: 'AkVariablesTextarea',

  /**
   * @type {Object}
   * @description A collection of components that this component
   * is dependant on.
   */
  components: {
    [Variables.name]: Variables,
    [Input.name]: Input,
    [Form.name]: Form,
    [Field.name]: Field,
  },

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    /**
     * @type {Number|String}
     * @description The value of the select input
     */
    modelValue: {
      default: undefined,
      type: String,
    },

    /**
     * @type {String}
     * @description The active account ID
     */
    account: {
      type: String,
      default: null,
    },

    /**
     * @type {String}
     * @description The active agency ID
     */
    agency: {
      type: String,
      default: null,
    },

    /**
     * @type {String}
     * @description Determines the position of the tooltip
     */
    placement: {
      type: String,
      default: 'bottom',
    },

    /**
     * @type {String}
     * @description The amount of padding around the content
     */
    size: {
      type: String,
      default: 'small',
    },

    /**
     * @type {String}
     * @description Determines the color of the popover background.
     */
    theme: {
      type: String,
      default: 'light',
    },

    /**
     * @type {String}
     * @description The number of rows for the textarea to display
     */
    rows: {
      type: String,
      default: '5',
    },
  },

  /**
   * @type {Array}
   * @description The list of events that this component can trigger
   */
  emits: ['update:modelValue'],

  /**
   * @param {Object} props - The properties of the component
   * @param {Object} options - The setup options
   * @param {Function} options.emit - The emit method for triggering events
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props, { emit }) {
    const selectionStart = ref(0)
    const selectionEnd = ref(0)
    const input = ref(null)

    /**
     * @type {Object}
     * @description Sets the get/set methods for the "computedValue"
     * property which is a String
     */
    const computedValue = computed({
      /**
       * @description Gets the "computedValue", this returns the
       * "currentValue" property
       */
      get() {
        return props.modelValue
      },

      /**
       * @description Sets the "computedValue", this sets the
       * "currentvalue" property, and will run the validation if
       * required
       */
      set(value) {
        emit('update:modelValue', value)
      },
    })

    /**
     * @param {Object} event.target
     * @description sets the selection start and end indexes for the
     * textarea
     */
    const setCursorPosition = ({ target }) => {
      const { selectionStart: start, selectionEnd: end } = target
      selectionStart.value = start
      selectionEnd.value = end
    }

    /**
     * @param {String} type
     * @param {Object} tag
     * @description Tries to pass the variable tag into the text area at the
     * current cursor position.
     */
    const insert = (tag) => {
      const start = computedValue.value.substring(0, selectionStart.value)
      const end = computedValue.value.substring(
        selectionEnd.value,
        computedValue.value.length
      )
      const newValue = `${start}${tag}${end}`
      input.value.computedValue = newValue
    }

    return {
      computedValue,
      selectionStart,
      selectionEnd,
      setCursorPosition,
      insert,
      input,
    }
  },
})
</script>

<style lang="scss">
@import '../../scss/_settings.scss';

.ak-variables-textarea {
  position: relative;
  padding-bottom: 25px;

  .ak-popover {
    position: absolute;
    left: 50%;
    bottom: 0;
  }
}
</style>
